import { useEffect, useState } from "react";
import axios from "axios";
import { Button, Modal, Form, Alert, Spinner } from "react-bootstrap";
import { Eye, EyeOff, CreditCard, ShieldCheck, Trash2 } from "lucide-react";
import { AppCheckbox, AppDateField } from "./form";
import { AvatarEditor, AvatarHoverControl, fileToAvatarDataUrl } from "./UserAvatar";
import { ROLE_OPTIONS, SHOWROOM_CITY_OPTIONS } from "../helpers/roles";
import { formatPhoneInput } from "../helpers/phone";
import "./UserFormModal.css";

const FOP_PREFIX = "ФОП ";

/** Дублює lib/scheduleGroup.js — лише для підказки у формі; на сервері група рахується так само. */
function scheduleGroupForRole(role, showroomCity) {
    if (role === "direct_manager") return "Дірект";
    if (role === "warehouse_manager") return "Склад";
    if (role === "showroom_manager") return { kyiv: "Шоурум Київ", kharkiv: "Шоурум Харків" }[showroomCity] || "";
    return "";
}

const emptyForm = () => ({
    lastName: "",
    firstName: "",
    patronymic: "",
    phone: "",
    username: "",
    password: "",
    birthDate: "",
    /** "crm:<id>" — роль KeyCRM, "app:<role>" — роль застосунку без CRM */
    roleKey: "app:manager",
    showroomCity: "",
    scheduleEnabled: false,
    payoutFullName: "",
    payoutIpn: "",
    payoutIban: "",
    keycrmManagerId: "",
    telegramId: "",
    fopEnabled: false,
    fopSlug: "",
    monobankToken: "",
    monoFopFullName: "",
    monoFopIpn: "",
    monoFopIban: "",
    monoFopCardKey: "",
    cardHolder: "",
    cardNumber: "",
    cardExpiry: "",
    novapayEnabled: false,
    novapayLogin: "",
    novapayAccountId: "",
    novapayRefreshToken: "",
    novapayPublicCertificate: "",
});

function formFromUser(user) {
    const p = user.profile || {};
    const integ = user.integrations || {};
    return {
        ...emptyForm(),
        lastName: p.lastName || "",
        firstName: p.firstName || "",
        patronymic: p.patronymic || "",
        phone: formatPhoneInput(p.phone || ""),
        username: user.username || "",
        birthDate: p.birthDate && /^\d{4}-\d{2}-\d{2}$/.test(p.birthDate) ? p.birthDate : "",
        roleKey: p.keycrmRoleId != null ? `crm:${p.keycrmRoleId}` : `app:${user.role || "manager"}`,
        showroomCity: p.showroomCity || "",
        scheduleEnabled: !!user.schedule,
        payoutFullName: user.payout?.fullName || "",
        payoutIpn: user.payout?.ipn || "",
        payoutIban: user.payout?.iban || "",
        keycrmManagerId: p.keycrmManagerId != null ? String(p.keycrmManagerId) : "",
        telegramId: p.telegramId != null ? String(p.telegramId) : "",
        fopEnabled: p.fopTitlePrefix === FOP_PREFIX,
        fopSlug: integ.fopSlug || "",
        monoFopFullName: integ.monoFop?.fullName || "",
        monoFopIpn: integ.monoFop?.ipn || "",
        monoFopIban: integ.monoFop?.iban || "",
        monoFopCardKey: integ.monoFop?.cardKey || "",
        novapayEnabled: !!integ.novapay?.enabled,
        novapayLogin: integ.novapay?.login || "",
        novapayAccountId: integ.novapay?.accountId != null ? String(integ.novapay.accountId) : "",
    };
}

const formatCardNumber = (v) =>
    v
        .replace(/\D/g, "")
        .slice(0, 19)
        .replace(/(\d{4})(?=\d)/g, "$1 ");

const formatExpiry = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 4);
    return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

const formatIban = (v) =>
    v
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, "")
        .slice(0, 29)
        .replace(/(.{4})(?=.)/g, "$1 ");

function Section({ title, children, aside }) {
    return (
        <section className="uf-section">
            <header className="uf-section__head">
                <h6 className="uf-section__title">{title}</h6>
                {aside}
            </header>
            <div className="uf-section__body">{children}</div>
        </section>
    );
}

/** Поле з підписом зверху; span — скільки колонок сітки (з 6) займає. */
function Field({ label, required, hint, span = 3, children }) {
    return (
        <Form.Group className={`uf-field uf-span-${span}`}>
            <Form.Label className="uf-label">
                {label}
                {required ? <span className="uf-required">*</span> : null}
            </Form.Label>
            {children}
            {hint ? <div className="uf-hint">{hint}</div> : null}
        </Form.Group>
    );
}

/** Реквізити: ПІБ, ІПН, IBAN (для ЗП або рахунку ФОП). */
function RequisitesFields({ form, set, prefix }) {
    return (
        <div className="uf-grid">
            <Field label="ПІБ отримувача" span={6}>
                <Form.Control value={form[`${prefix}FullName`]} onChange={(e) => set(`${prefix}FullName`, e.target.value)} />
            </Field>
            <Field label="ІПН" span={2}>
                <Form.Control
                    inputMode="numeric"
                    maxLength={10}
                    value={form[`${prefix}Ipn`]}
                    onChange={(e) => set(`${prefix}Ipn`, e.target.value.replace(/\D/g, ""))}
                />
            </Field>
            <Field label="IBAN" span={4}>
                <Form.Control
                    placeholder="UA00 0000 0000 0000 0000 0000 0000 0"
                    value={formatIban(form[`${prefix}Iban`])}
                    onChange={(e) => set(`${prefix}Iban`, e.target.value.replace(/\s/g, "").toUpperCase())}
                />
            </Field>
        </div>
    );
}

/**
 * Форма співробітника (створення / редагування).
 * keycrmRoles — довідник ролей KeyCRM [{ roleId, name, appRole }].
 */
export default function UserFormModal({ show, mode, user, keycrmRoles, onHide, onSaved, onUserPatched }) {
    const [form, setForm] = useState(emptyForm);
    const [avatarDataUrl, setAvatarDataUrl] = useState("");
    const [replaceCard, setReplaceCard] = useState(false);
    const [clearCard, setClearCard] = useState(false);
    const [revealedCard, setRevealedCard] = useState(null);
    const [clearMonobankToken, setClearMonobankToken] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!show) return;
        setForm(mode === "edit" && user ? formFromUser(user) : emptyForm());
        setAvatarDataUrl("");
        setReplaceCard(false);
        setClearCard(false);
        setRevealedCard(null);
        setClearMonobankToken(false);
        setError("");
        // Відкриваємо форму заново лише при показі / зміні користувача, не при кожному оновленні об'єкта
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, mode, user?._id]);

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    // Роль, збережена як app:<role>, для якої є роль CRM, показуємо (і зберігаємо) як відповідний пункт CRM
    const roleKey = (() => {
        const [src, val] = form.roleKey.split(":");
        const mapped = src === "app" ? keycrmRoles.find((r) => r.appRole === val) : null;
        return mapped ? `crm:${mapped.roleId}` : form.roleKey;
    })();
    const [roleSource, roleValue] = roleKey.split(":");
    const crmRole = roleSource === "crm" ? keycrmRoles.find((r) => String(r.roleId) === roleValue) : null;
    const effectiveRole = crmRole ? crmRole.appRole : roleValue;
    const scheduleGroup = scheduleGroupForRole(effectiveRole, form.showroomCity);
    const hasStoredCard = !!user?.integrations?.monoCard?.last4 && !clearCard;
    const showCardInputs = mode === "create" || !hasStoredCard || replaceCard;

    const pickAvatar = async (file) => {
        try {
            setAvatarDataUrl(await fileToAvatarDataUrl(file));
        } catch (err) {
            setError(err.message);
        }
    };

    const revealCard = async () => {
        if (revealedCard) {
            setRevealedCard(null);
            return;
        }
        try {
            const res = await axios.get(`/api/users/${user._id}/mono-card`, { withCredentials: true });
            setRevealedCard(res.data);
        } catch (err) {
            setError(err.response?.data?.error || "Не вдалося показати дані картки");
        }
    };

    const buildPayload = () => {
        const body = {
            lastName: form.lastName,
            firstName: form.firstName,
            patronymic: form.patronymic,
            phone: form.phone,
            username: form.username,
            birthDate: form.birthDate || "",
            showroomCity: effectiveRole === "showroom_manager" ? form.showroomCity : "",
            scheduleEnabled: form.scheduleEnabled,
            payoutFullName: form.payoutFullName,
            payoutIpn: form.payoutIpn,
            payoutIban: form.payoutIban,
            keycrmManagerId: form.keycrmManagerId,
            telegramId: form.telegramId,
            fopTitlePrefix: form.fopEnabled ? FOP_PREFIX : "",
        };
        if (roleSource === "crm") body.keycrmRoleId = Number(roleValue);
        else {
            body.role = roleValue;
            body.keycrmRoleId = null;
        }
        if (form.password) body.password = form.password;

        if (form.fopEnabled) {
            Object.assign(body, {
                fopSlug: form.fopSlug,
                monoFopFullName: form.monoFopFullName,
                monoFopIpn: form.monoFopIpn,
                monoFopIban: form.monoFopIban,
                monoFopCardKey: form.monoFopCardKey,
                novapayEnabled: form.novapayEnabled,
            });
            if (clearMonobankToken) body.monobankToken = "";
            else if (form.monobankToken.trim()) body.monobankToken = form.monobankToken.trim();
            if (clearCard) body.monoCardClear = true;
            else if (showCardInputs && (form.cardNumber || form.cardHolder || form.cardExpiry)) {
                body.monoCard = { holder: form.cardHolder, number: form.cardNumber, expiry: form.cardExpiry };
            }
            if (form.novapayEnabled) {
                Object.assign(body, {
                    novapayLogin: form.novapayLogin,
                    novapayAccountId: form.novapayAccountId,
                    novapayRefreshToken: form.novapayRefreshToken,
                    novapayPublicCertificate: form.novapayPublicCertificate,
                });
            }
        } else {
            // ФОП вимкнено: дані ФОП лишаються в БД, але NovaPay-рахунок перестає підтягуватись
            body.novapayEnabled = false;
        }
        return body;
    };

    const validate = () => {
        const missing = [];
        if (!form.lastName.trim()) missing.push("прізвище");
        if (!form.firstName.trim()) missing.push("ім'я");
        if (!form.phone.trim()) missing.push("телефон");
        if (!form.username.trim()) missing.push("email");
        if (mode === "create" && !form.password) missing.push("пароль");
        if (effectiveRole === "showroom_manager" && !form.showroomCity) missing.push("місто шоуруму");
        return missing.length ? `Заповніть: ${missing.join(", ")}` : "";
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        const problem = validate();
        if (problem) {
            setError(problem);
            return;
        }
        setSaving(true);
        setError("");
        try {
            const body = buildPayload();
            const res =
                mode === "create"
                    ? await axios.post("/api/users", body, { withCredentials: true })
                    : await axios.put(`/api/users/${user._id}`, body, { withCredentials: true });
            let warning = res.data?.warning || "";
            if (mode === "create" && avatarDataUrl) {
                try {
                    await axios.put(`/api/users/${res.data._id}/avatar`, { image: avatarDataUrl }, { withCredentials: true });
                } catch (err) {
                    warning = [warning, "Фото не збережено"].filter(Boolean).join(". ");
                }
            }
            onSaved?.(warning);
        } catch (err) {
            setError(err.response?.data?.error || "Помилка збереження");
        } finally {
            setSaving(false);
        }
    };

    /**
     * Один список ролей: кожна роль застосунку — один пункт. Якщо їй відповідає роль KeyCRM (довідник «Ролі CRM»),
     * пункт прив'язаний до неї (crm:<id>, назва з CRM), інакше — роль без CRM (app:<role>).
     * Кілька ролей CRM з тими самими правами не дублюються: береться поточна роль співробітника або перша.
     */
    const roleOptions = ROLE_OPTIONS.map((o) => {
        const mapped = keycrmRoles.filter((r) => r.appRole === o.value);
        if (!mapped.length) return { value: `app:${o.value}`, label: o.label };
        const pick = mapped.find((r) => `crm:${r.roleId}` === roleKey) || mapped[0];
        return { value: `crm:${pick.roleId}`, label: pick.name || o.label };
    });

    return (
        <Modal show={show} onHide={onHide} centered size="lg" scrollable className="user-form">
            <Modal.Header closeButton>
                <Modal.Title>{mode === "create" ? "Новий співробітник" : "Редагувати співробітника"}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    {error ? <Alert variant="danger" className="py-2 mb-3">{error}</Alert> : null}

                    <Section title="Особисті дані">
                        <div className="uf-person">
                            <div className="uf-person__avatar">
                                {mode === "edit" && user ? (
                                    <AvatarEditor user={user} size={88} onChange={(updated) => onUserPatched?.(updated)} />
                                ) : (
                                    <AvatarHoverControl
                                        user={{ _id: "new", profile: { firstName: form.firstName || "?", lastName: form.lastName } }}
                                        size={88}
                                        previewSrc={avatarDataUrl}
                                        onPick={pickAvatar}
                                        onRemove={() => setAvatarDataUrl("")}
                                    />
                                )}
                            </div>
                            <div className="uf-grid uf-person__fields">
                                <Field label="Прізвище" required span={6}>
                                    <Form.Control value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
                                </Field>
                                <Field label="Ім'я" required span={6}>
                                    <Form.Control value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
                                </Field>
                                <Field label="По батькові" span={6}>
                                    <Form.Control value={form.patronymic} onChange={(e) => set("patronymic", e.target.value)} />
                                </Field>
                                <Field label="Телефон" required>
                                    <Form.Control
                                        type="tel"
                                        inputMode="tel"
                                        placeholder="(068) 080 18 86"
                                        value={form.phone}
                                        onChange={(e) => set("phone", formatPhoneInput(e.target.value))}
                                    />
                                </Field>
                                <Field label="Дата народження">
                                    <AppDateField value={form.birthDate} onChange={(v) => set("birthDate", v)} />
                                </Field>
                            </div>
                        </div>
                    </Section>

                    <Section title="Вхід у застосунок">
                        <div className="uf-grid">
                            <Field label="Email (логін)" required>
                                <Form.Control
                                    type="email"
                                    autoComplete="off"
                                    value={form.username}
                                    disabled={mode === "edit"}
                                    onChange={(e) => set("username", e.target.value)}
                                />
                            </Field>
                            <Field
                                label={
                                    mode === "edit" && !user?.needsPassword ? (
                                        <>
                                            Пароль <span className="fw-normal">(порожньо — не змінювати)</span>
                                        </>
                                    ) : (
                                        "Пароль"
                                    )
                                }
                                required={mode === "create"}
                                hint={mode === "edit" && user?.needsPassword ? "Ще не задано — введіть такий, як у CRM" : null}
                            >
                                <Form.Control
                                    type="password"
                                    autoComplete="new-password"
                                    value={form.password}
                                    onChange={(e) => set("password", e.target.value)}
                                />
                            </Field>
                        </div>
                    </Section>

                    <Section title="Роль і графік">
                        <div className="uf-grid">
                            <Field label="Роль" span={effectiveRole === "showroom_manager" ? 4 : 6}>
                                <Form.Select value={roleKey} onChange={(e) => set("roleKey", e.target.value)}>
                                    {roleOptions.map((r) => (
                                        <option key={r.value} value={r.value}>
                                            {r.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Field>
                            {effectiveRole === "showroom_manager" ? (
                                <Field label="Місто" required span={2}>
                                    <Form.Select value={form.showroomCity} onChange={(e) => set("showroomCity", e.target.value)}>
                                        <option value="">Оберіть…</option>
                                        {SHOWROOM_CITY_OPTIONS.map((c) => (
                                            <option key={c.value} value={c.value}>
                                                {c.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Field>
                            ) : null}
                            <div className="uf-span-6 uf-check-row">
                                <AppCheckbox
                                    id="user-schedule-enabled"
                                    label="Показувати в графіку змін"
                                    checked={form.scheduleEnabled}
                                    onChange={(e) => set("scheduleEnabled", e.target.checked)}
                                />
                                <span className="uf-pill">{scheduleGroup || "Загальна група"}</span>
                            </div>
                        </div>
                    </Section>

                    <Section title="Реквізити для ЗП">
                        <RequisitesFields form={form} set={set} prefix="payout" />
                    </Section>

                    <Section title="Інтеграції">
                        <div className="uf-grid">
                            <Field label="KeyCRM manager id">
                                <Form.Control type="number" value={form.keycrmManagerId} onChange={(e) => set("keycrmManagerId", e.target.value)} />
                            </Field>
                            <Field label="Telegram ID">
                                <Form.Control value={form.telegramId} onChange={(e) => set("telegramId", e.target.value)} />
                            </Field>
                        </div>
                    </Section>

                    <section className={`uf-toggle-card${form.fopEnabled ? " uf-toggle-card--on" : ""}`}>
                        <div className="uf-toggle-card__head">
                            <AppCheckbox
                                id="user-fop-enabled"
                                label="ФОП"
                                checked={form.fopEnabled}
                                onChange={(e) => set("fopEnabled", e.target.checked)}
                            />
                            <span className="uf-hint m-0">Префікс «ФОП» у підписі, Monobank, NovaPay</span>
                        </div>

                        {form.fopEnabled ? (
                            <div className="uf-toggle-card__body">
                                <h6 className="uf-subtitle">Monobank</h6>
                                <div className="uf-grid">
                                    <Field label="Ключ Mono / API (fopSlug)">
                                        <Form.Control placeholder="iryna, andrii, …" value={form.fopSlug} onChange={(e) => set("fopSlug", e.target.value)} />
                                    </Field>
                                    <Field
                                        label="Monobank X-Token"
                                        hint={user?.integrationsMeta?.hasMonobankToken ? "Збережено. Порожньо — не змінювати" : null}
                                    >
                                        <Form.Control
                                            type="password"
                                            autoComplete="new-password"
                                            value={form.monobankToken}
                                            onChange={(e) => set("monobankToken", e.target.value)}
                                        />
                                    </Field>
                                    {mode === "edit" && user?.integrationsMeta?.hasMonobankToken ? (
                                        <div className="uf-span-6">
                                            <AppCheckbox
                                                id="clear-mono-token"
                                                label="Скинути токен Monobank"
                                                checked={clearMonobankToken}
                                                onChange={(e) => setClearMonobankToken(e.target.checked)}
                                            />
                                        </div>
                                    ) : null}
                                </div>

                                <h6 className="uf-subtitle">Реквізити рахунку ФОП</h6>
                                <RequisitesFields form={form} set={set} prefix="monoFop" />
                                <div className="uf-grid mt-3">
                                    <Field label="Ключ карта" span={6}>
                                        <Form.Control
                                            autoComplete="off"
                                            value={form.monoFopCardKey.replace(/\s+/g, "").replace(/(.{4})(?=.)/g, "$1 ")}
                                            onChange={(e) => set("monoFopCardKey", e.target.value.replace(/\s+/g, ""))}
                                        />
                                    </Field>
                                </div>

                                <h6 className="uf-subtitle d-flex align-items-center justify-content-between">
                                    Біла картка Mono
                                    <span className="uf-hint m-0 d-inline-flex align-items-center gap-1 fw-normal">
                                        <ShieldCheck size={13} aria-hidden /> зберігається зашифровано
                                    </span>
                                </h6>
                                {hasStoredCard && !replaceCard ? (
                                    <div className="uf-card-stored">
                                        <CreditCard size={18} aria-hidden />
                                        <div className="flex-grow-1">
                                            {revealedCard ? (
                                                <>
                                                    <div className="fw-semibold">{formatCardNumber(revealedCard.number)}</div>
                                                    <div className="uf-hint m-0">
                                                        {revealedCard.holder || "—"} · {revealedCard.expiry}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="fw-semibold">•••• {user.integrations.monoCard.last4}</div>
                                            )}
                                        </div>
                                        <button type="button" className="uf-icon-btn" onClick={revealCard} title={revealedCard ? "Сховати" : "Показати"}>
                                            {revealedCard ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => setReplaceCard(true)}>
                                            Замінити
                                        </Button>
                                        <button type="button" className="uf-icon-btn uf-icon-btn--danger" title="Видалити дані картки" onClick={() => setClearCard(true)}>
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ) : null}
                                {showCardInputs ? (
                                    <div className="uf-grid">
                                        <Field label="Ім'я на картці" span={6}>
                                            <Form.Control autoComplete="off" value={form.cardHolder} onChange={(e) => set("cardHolder", e.target.value)} />
                                        </Field>
                                        <Field label="Номер картки" span={4}>
                                            <Form.Control
                                                inputMode="numeric"
                                                autoComplete="off"
                                                placeholder="0000 0000 0000 0000"
                                                value={formatCardNumber(form.cardNumber)}
                                                onChange={(e) => set("cardNumber", e.target.value.replace(/\D/g, ""))}
                                            />
                                        </Field>
                                        <Field label="Діє до" span={2} hint="CVV не зберігаємо">
                                            <Form.Control
                                                inputMode="numeric"
                                                autoComplete="off"
                                                placeholder="ММ/РР"
                                                value={form.cardExpiry}
                                                onChange={(e) => set("cardExpiry", formatExpiry(e.target.value))}
                                            />
                                        </Field>
                                        {replaceCard ? (
                                            <div className="uf-span-6">
                                                <Button size="sm" variant="link" className="p-0" onClick={() => setReplaceCard(false)}>
                                                    Скасувати заміну
                                                </Button>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                                {clearCard ? (
                                    <div className="uf-hint text-danger">
                                        Дані картки буде видалено після збереження.{" "}
                                        <Button size="sm" variant="link" className="p-0 align-baseline" onClick={() => setClearCard(false)}>
                                            Скасувати
                                        </Button>
                                    </div>
                                ) : null}

                                <section className={`uf-toggle-card uf-toggle-card--nested${form.novapayEnabled ? " uf-toggle-card--on" : ""}`}>
                                    <div className="uf-toggle-card__head">
                                        <AppCheckbox
                                            id="user-novapay-enabled"
                                            label="NovaPay"
                                            checked={form.novapayEnabled}
                                            onChange={(e) => set("novapayEnabled", e.target.checked)}
                                        />
                                        {user?.novapayStatus ? (
                                            <span className={`uf-status ${user.novapayStatus.connected ? "uf-status--ok" : "uf-status--bad"}`}>
                                                {user.novapayStatus.connected
                                                    ? "підключено"
                                                    : user.novapayStatus.authError
                                                      ? "ключі відхилено"
                                                      : "ключі не введено"}
                                            </span>
                                        ) : null}
                                    </div>
                                    {form.novapayEnabled ? (
                                        <div className="uf-toggle-card__body uf-grid">
                                            <Field label="Логін бізнес-акаунта">
                                                <Form.Control autoComplete="off" placeholder="NaumenkoYaYu" value={form.novapayLogin} onChange={(e) => set("novapayLogin", e.target.value)} />
                                            </Field>
                                            <Field label="ID рахунку (accountId)">
                                                <Form.Control type="number" value={form.novapayAccountId} onChange={(e) => set("novapayAccountId", e.target.value)} />
                                            </Field>
                                            <Field label="refresh_token" span={6} hint="Кабінет NovaPay → Система → Налаштування → API. Порожньо — не змінювати">
                                                <Form.Control as="textarea" rows={2} autoComplete="off" value={form.novapayRefreshToken} onChange={(e) => set("novapayRefreshToken", e.target.value)} />
                                            </Field>
                                            <Field label="public_certificate" span={6}>
                                                <Form.Control as="textarea" rows={3} autoComplete="off" value={form.novapayPublicCertificate} onChange={(e) => set("novapayPublicCertificate", e.target.value)} />
                                            </Field>
                                        </div>
                                    ) : null}
                                </section>
                            </div>
                        ) : null}
                    </section>
                    <button type="submit" hidden aria-hidden />
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>
                    Скасувати
                </Button>
                <Button variant="dark" onClick={handleSubmit} disabled={saving}>
                    {saving ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                    Зберегти
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
